import { Box, Menu, MenuItem, Tooltip, Divider } from "@mui/material";
import { differenceInDays, parseISO } from "date-fns";
import * as React from "react";
import { PoliceEvent } from "../types/Polices";

import {
	PermissionActionsEnum,
	PermissionRolesEnum,
} from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";

export type PoliceMenuAction =
	| "details"
	| "cancel"
	| "print-termo"
	| "print-contratada"
	| "print-apolice"
	| "print-endosso"
	| "renew";

interface Props {
	anchorEl: HTMLElement | null;
	open: boolean;
	onClose: () => void;
	selectedRow: PoliceEvent | null;
	onSelect: (action: PoliceMenuAction) => void;
}

// ----------------------------------
// NORMALIZAÇÃO DE STATUS
// ----------------------------------
type PoliceStatusNorm = "pending" | "in_force" | "expired" | "canceled" | "other";

const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

function normalizePoliceStatus(raw?: string | null): PoliceStatusNorm {
	const n = norm(raw);
	if (["pending", "pendente", "aguardando_emissao"].includes(n)) return "pending";
	if (["in_force", "vigente", "em_vigor", "open"].includes(n)) return "in_force";
	if (["expired", "expirada", "vencida"].includes(n)) return "expired";
	if (["canceled", "cancelada"].includes(n)) return "canceled";
	if (["to_invoice", "apolice a faturar"].includes(n)) return "in_force"; // Trata como vigente para permitir cancelamento
	return "other";
}

// ----------------------------------
// PERMISSÃO + STATUS PARA CANCELAR
// ----------------------------------
function computeCancelPermission(row: PoliceEvent | null) {
	// 1) Sem linha selecionada
	if (!row) {
		return {
			canCancel: false,
			reasonCancel: "Selecione uma apólice.",
		};
	}

	// 2) Sem permissão de cancelar
	const hasCancelPermission = checkByProfileGuard(
		PermissionRolesEnum.FirePolicesOptions,
		PermissionActionsEnum.Cancel
	);

	if (!hasCancelPermission) {
		return {
			canCancel: false,
			reasonCancel: "Você não tem permissão para cancelar apólices.",
		};
	}

	// 3) Com permissão, avaliar status
	const status = normalizePoliceStatus(row.status);

	if (status !== "in_force") {
		return {
			canCancel: false,
			reasonCancel: `Só é possível cancelar apólices vigentes ou a faturar. Status atual: "${row.status}".`,
		};
	}

	// 4) Ok para cancelar
	return {
		canCancel: true,
		reasonCancel: "",
	};
}

// ----------------------------------
// PERMISSÃO + STATUS PARA RENOVAR
// ----------------------------------
function computeRenewPermission(row: PoliceEvent | null) {
	// 1) Sem linha selecionada
	if (!row) {
		return {
			canRenew: false,
			reasonRenew: "Selecione uma apólice.",
		};
	}

	// 2) Sem permissão de renovar
	const hasRenewPermission = checkByProfileGuard(
		PermissionRolesEnum.FirePolicesOptions,
		PermissionActionsEnum.Renew
	);

	if (!hasRenewPermission) {
		return {
			canRenew: false,
			reasonRenew: "Você não tem permissão para renovar apólices.",
		};
	}

	// 3) Validar data de vencimento (<= 30 dias)
	if (!row.validity_end) {
		return {
			canRenew: false,
			reasonRenew: "Data de validade não encontrada.",
		};
	}

	const days = differenceInDays(parseISO(row.validity_end), new Date());

	if (days > 30) {
		return {
			canRenew: false,
			reasonRenew: `Renovação permitida apenas 30 dias antes do vencimento. Faltam ${days} dias.`,
		};
	}

	return {
		canRenew: true,
		reasonRenew: "",
	};
}

// ----------------------------------
// TOOLTIP PARA DISABLED
// ----------------------------------
const TooltipIfDisabled: React.FC<{
	title: string;
	disabled: boolean;
	children: React.ReactNode;
}> = ({ title, disabled, children }) =>
		disabled ? (
			<Tooltip title={title}>
				<span style={{ display: "block" }}>{children}</span>
			</Tooltip>
		) : (
			<>{children}</>
		);

// ----------------------------------
// MENU
// ----------------------------------
export const PoliceMenu: React.FC<Props> = ({
	anchorEl,
	open,
	onClose,
	selectedRow,
	onSelect,
}) => {
	const { canCancel, reasonCancel } = computeCancelPermission(selectedRow);
	const { canRenew, reasonRenew } = computeRenewPermission(selectedRow);

	const canPrint = checkByProfileGuard(
		PermissionRolesEnum.FirePolicesOptions,
		PermissionActionsEnum.Print
	);

	const handle = (action: PoliceMenuAction) => {
		if (selectedRow) onSelect(action);
		onClose();
	};

	return (
		<Menu anchorEl={anchorEl} open={open} onClose={onClose}>
			<Box>
				{/* Detalhes */}
				<MenuItem onClick={() => handle("details")}>Detalhes da Apólice</MenuItem>

				{/* Renovar */}
				<TooltipIfDisabled title={reasonRenew} disabled={!canRenew}>
					<MenuItem disabled={!canRenew} onClick={() => handle("renew")}>
						Renovar Apólice
					</MenuItem>
				</TooltipIfDisabled>

				{/* Cancelamento (perm + status) */}
				<TooltipIfDisabled title={reasonCancel} disabled={!canCancel}>
					<MenuItem disabled={!canCancel} onClick={() => handle("cancel")}>
						Cancelar Apólice
					</MenuItem>
				</TooltipIfDisabled>

				<Divider sx={{ my: 1 }} />

				{/* Impressões */}
				<TooltipIfDisabled title="Você não tem permissão para imprimir." disabled={!canPrint}>
					<Box>
						<MenuItem disabled={!canPrint} onClick={() => handle("print-termo")}>
							Imprimir Termo de Adesão
						</MenuItem>
						{/* <MenuItem disabled={!canPrint} onClick={() => handle("print-contratada")}>
							Imprimir Proposta Contratada
						</MenuItem> */}
						<MenuItem disabled={!canPrint} onClick={() => handle("print-apolice")}>
							Imprimir Apólice
						</MenuItem>
						<MenuItem disabled={!canPrint} onClick={() => handle("print-endosso")}>
							Imprimir Endosso Cancelamento
						</MenuItem>
					</Box>
				</TooltipIfDisabled>
			</Box>
		</Menu>
	);
};
